import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  Method,
  AxiosProgressEvent,
} from "axios";
import Config from "../Config";

const getAuthToken = (): string | null => {
  return localStorage.getItem("jwt-token");
};

type ProgressCallback = (progressEvent: AxiosProgressEvent) => void;

interface ApiRequestOptions {
  method: Method;
  path: string;
  data?: any;
  token?: boolean;
  contentType?: string;
  progressCallback?: ProgressCallback | null;
}

const apiRequest = async ({
  method,
  path,
  data = {},
  token = true,
  contentType = "application/json",
  progressCallback = null,
}: ApiRequestOptions): Promise<AxiosResponse<any>> => {
  const headers: Record<string, string> = {
    "Content-Type": contentType,
    ...(token ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
  };

  const scheme = Config.DISABLE_SSL ? "http" : "https";

  const config: AxiosRequestConfig = {
    method,
    url: `${scheme}://${Config.HOSTNAME}/api${path}`,
    headers,
    data,
    onUploadProgress: progressCallback ?? ((_: AxiosProgressEvent) => {}), // fallback empty function
  };

  try {
    const response = await axios(config);

    if (response.status >= 200 && response.status < 300) {
      return response;
    } else {
      const error = new Error(
        `Request failed with status ${response.status}`
      ) as any;
      error.response = response;
      throw error;
    }
  } catch (error) {
    throw error;
  }
};

const get = (path: string, token = true, contentType = "application/json") =>
  apiRequest({ method: "GET", path, token, contentType });

const post = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json",
  progressCallback: ProgressCallback | null = null
) =>
  apiRequest({
    method: "POST",
    path,
    data,
    token,
    contentType,
    progressCallback,
  });

const del = (path: string, token = true, contentType = "application/json") =>
  apiRequest({ method: "DELETE", path, token, contentType });

const patch = (
  path: string,
  data: any,
  token = true,
  contentType = "application/json"
) => apiRequest({ method: "PATCH", path, data, token, contentType });

// Auth endpoints
export const exchangeKeycloakCode = (code: string) =>
  post(`/auth/keycloak/callback/?code=${code}`, {}, false);
export const getKeycloakVars = () => get("/auth/keycloak/vars/", false);

export const login = (email: string, password: string) => {
  const form = new URLSearchParams();
  form.append("username", email);
  form.append("password", password);
  return post("/auth/token/", form, false, "application/x-www-form-urlencoded");
};

export const signup = (email: string, password: string) => {
  const form = new URLSearchParams();
  form.append("username", email);
  form.append("password", password);
  return post(
    "/auth/signup/",
    form,
    false,
    "application/x-www-form-urlencoded"
  );
};

// Document endpoints
export const addDocument = (name: string) =>
  post(`/document/?name=${name}`, {});
export const addPage = (
  documentID: string,
  file: File,
  progressCallback: ProgressCallback | null = null
) => {
  const form = new FormData();
  form.append("file", file);
  return post(
    `/document/${documentID}/pages/`,
    form,
    true,
    "multipart/form-data",
    progressCallback
  );
};

export const getDocument = (documentID: string) =>
  get(`/document/${documentID}/`);
export const getDocumentPages = (documentID: string) =>
  get(`/document/${documentID}/pages/`);
export const getPageData = (pageID: string) =>
  get(`/document/1234/pages/${pageID}/?data=true`);
export const getPageImage = (pageID: string) =>
  get(`/document/1234/pages/${pageID}/?image=true`);

export const deleteDocument = (documentID: string) =>
  del(`/document/${documentID}/`);
export const deletePage = (pageID: string) =>
  del(`/document/1234/pages/${pageID}/`);
export const renameDocument = (extractionId: string, newName: string) =>
  patch(`/document/${extractionId}/?name=${newName}`, {});

export const getJobList = (searchQuery: string) =>
  get(`/document/?thumbnail=true&query="${searchQuery}"`);

export const getMoleculeSVG = (
  transcription: string,
  depictParams: Record<string, string> = {},
  token = false
) => {
  const defaultParams = {
    abbr: "off",
    hdisp: "S",
    zoom: "1.3",
    annotate: "none",
    r: "0",
    ...depictParams,
  };
  const allParams = new URLSearchParams({
    smi: transcription,
    ...defaultParams,
  });
  return get(`/depict?${allParams.toString()}`, token, "application/json");
};
